import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import logger from '../../utils/logger';
import { logout } from '../../resources/api.resource';
import { getLoginAgainUrl } from '../../utils/loginUrl';

interface SessionExpiredModalProps {
  open: boolean;
  onConfirm?: () => void;
}

const SessionExpiredModal: React.FC<SessionExpiredModalProps> = ({ open, onConfirm }) => {
  const handleLogout = async () => {
    logger.event('Session expired - User logging out');
    onConfirm?.();

    // Best-effort server-side cleanup. The backend's logoutUrl is intended for
    // explicit user-initiated logout and points at the branded sign-in page;
    // for session-expired we want a direct SSO restart on the same tenant, so
    // we always navigate via getLoginAgainUrl() regardless of the response.
    try {
      await logout();
    } catch (error) {
      logger.log('Session-expired logout cleanup failed (best-effort)', { error });
    }

    window.location.href = getLoginAgainUrl();
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="session-expired-dialog-title"
      aria-describedby="session-expired-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="session-expired-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Session Expired
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="session-expired-dialog-description">
          Your session has expired. Please log in again to continue using the application.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleLogout}
          color="primary"
          variant="contained"
          fullWidth
          autoFocus
        >
          Log In Again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiredModal;
